import React, { useState, useEffect } from 'react';
import { Copy, MessageSquare, Receipt, Share, AlertTriangle, CheckCircle, X, ArrowUpRight, ArrowDownLeft, Clock, User, DollarSign, Calendar, Gift, CreditCard, ArrowDownCircle, ArrowUpCircle, QrCode } from 'lucide-react';
import QRCode from 'react-qr-code';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { Separator } from '@vaiu/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Transaction, platformApi } from '@/services/platformApi';
import { CurrencyDisplay } from '@vaiu/ui';
import { StatusBadge } from '@vaiu/ui';
import TokenCard from '@/components/TokenCard';
import WhatsAppStyleMessaging from './WhatsAppStyleMessaging';
import { useAuth } from '@/context/AuthContext';
import { usePrepaidTapToPay } from '@/hooks/usePrepaidTapToPay';


interface TransactionDetailsProps {
  transaction: Transaction;
  onClose: () => void;
}

interface DetailedTokenInfo {
  token: {
    key: string;
    amount: number;
    currency: string;
    status: string;
    message?: string;
    type: string;
    createdAt: string;
    date?: string; // Legacy field
    created_at?: string; // Snake case variant
    dateCreated?: string; // Camel case variant
    updatedAt?: string;
    expiresAt?: string;
    completedAt?: string;
    redeemedAt?: string;
    createdBy?: string;
    redeemedBy?: string;
  };
  invoiceMetadata?: {
    createdByCurrentUser: boolean;
    createdBy: string;
    currentUserId: string;
    totalPayments: number;
    totalPaid: number;
    remainingAmount: number;
    originalAmount: number;
    isFullyPaid: boolean;
  };
  redemptionDetails?: {
    redeemedAt: string;
    redeemedBy: string;
    redeemedAmount: number;
    netAmount: number;
    fees: number;
    transactionId: string;
  };
  referralReward?: {
    isReferralToken: boolean;
    rewardAmount: number;
    rewardCurrency: string;
    rewardGiven: boolean;
    rewardGivenAt?: string;
    newUserReward: boolean;
  };
  invoicePayments?: Array<{
    paymentId: string;
    paidAt: string;
    paidBy: string;
    amount: number;
    currency: string;
    paymentMethod: string;
    // Funding source tracking removed
    transactionId: string;
    tokenKey?: string; // Added for cash tokens
    message?: string; // Added for cash tokens
    paidByCurrentUser?: boolean; // Added to identify user's payments
    paymentMethodDetails?: any; // Added for payment method details
  }>;
  prepaidCard?: {
    tokenId: string;
    uniqueUserId: string;
    cardNumber: string;
    cardExpMth: number;
    cardExpYear: number;
    cardCvv: number;
    cardHolderName: string;
    status: string;
    cardCurrency?: string;
    amountLoaded: number;
    currency: string;
    createdAt?: string;
    convertedAt?: string;
    cardBrand?: 'visa' | 'mastercard';
  };
}

function formatPartyUserIdForDisplay(
  value: string | undefined,
  currentUserId: string | undefined
): string | undefined {
  if (!value) return undefined;
  if (currentUserId && value === currentUserId) return 'me';
  return value;
}

const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  transaction,
  onClose
}) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [showRawData, setShowRawData] = useState(false);
  const [detailedTokenInfo, setDetailedTokenInfo] = useState<DetailedTokenInfo | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showMessaging, setShowMessaging] = useState(false);
  const prepaidTapToPay = usePrepaidTapToPay();

  // Load detailed token information for invoice tokens and completed tokens
  useEffect(() => {
    async function loadDetailedInfo() {
      if (!transaction?.tokenKey) return;
      
      // Only load additional details for invoice tokens to show payment history
      // and only if we created the invoice (to see payments received)
      const isInvoiceToken = transaction.type === 'invoice' || transaction.tokenType === 'invoice';
      const isInvoiceCreator = transaction.createdBy === user?._id;
      
      if (isInvoiceToken && isInvoiceCreator) {
        setLoadingDetails(true);
        try {
          const details = await platformApi.getTokenDetails(transaction.tokenKey);
          setDetailedTokenInfo(details);
          console.log('🔍 Invoice token payment history loaded:', details);
        } catch (error) {
          console.error('Failed to load invoice payment history:', error);
          // Don't show error to user, just continue with basic info
        } finally {
          setLoadingDetails(false);
        }
      } else {
        // For non-invoice tokens or if we're not the invoice creator,
        // use the transaction data directly
        setDetailedTokenInfo({
          token: {
            key: transaction?.tokenKey || '',
            amount: transaction?.amount || 0,
            currency: transaction?.currency || 'USD',
            status: transaction?.status || 'unknown',
            message: transaction?.message || transaction?.description || '',
            type: transaction?.type || 'unknown',
            createdAt: transaction?.createdAt || '',
            updatedAt: transaction?.updatedAt || '',
            expiresAt: transaction?.expiresAt || '',
            completedAt: transaction?.completedAt || '',
            redeemedAt: transaction?.redeemedAt || '',
            createdBy: transaction?.createdBy || '',
            redeemedBy: transaction?.redeemedBy || ''
          }
        });
      }
    }

    void loadDetailedInfo();
  }, [transaction]);

  const getTransactionTypeDisplay = () => {
    if (!transaction) return { label: 'Unknown', color: 'text-gray-600' };

    const transactionType = transaction?.type as string;
    const description = transaction?.description || transaction?.message || '';
    
    // Check if this is an invoice token based on various indicators
    const isInvoiceToken = transactionType === 'invoice' || 
                          description.toLowerCase().includes('invoice') ||
                          (transaction as any)?.tokenType === 'invoice' ||
                          (transaction as any)?.invoiceToken;
    
    // Check if this is a data token
    const isDataToken = transactionType === 'data' || 
                       description.toLowerCase().includes('data token');
    
    // Check if this is an identity token
    const isIdentityToken = transactionType === 'identity' || 
                           description.toLowerCase().includes('identity token');
    
    if (isInvoiceToken) {
      switch (transactionType) {
        case 'send':
          return { label: 'Invoice token', color: 'text-orange-600' };
        case 'receive':
          return { label: 'Invoice token', color: 'text-orange-600' };
        case 'debit':
          return { label: 'Invoice token', color: 'text-orange-600' };
        case 'credit':
          return { label: 'Invoice token', color: 'text-orange-600' };
        case 'redeem':
          return { label: 'Invoice token', color: 'text-orange-600' };
        default:
          return { label: 'Invoice token', color: 'text-orange-600' };
      }
    }
    
    if (isDataToken) {
      return { label: 'Data token', color: 'text-purple-600' };
    }
    
    if (isIdentityToken) {
      return { label: 'Identity token', color: 'text-indigo-600' };
    }
    
    // Check if this is a balance_up (topup) transaction
    const tokenType = (transaction as any)?.tokenType;
    if (tokenType === 'balance_up' || tokenType === 'topup') {
      return { label: 'Balance Top-up', color: 'text-green-600' };
    }
    
    switch (transactionType) {
      case 'send':
        return { label: 'Cash token', color: 'text-red-600' };
      case 'receive':
        return { label: 'Cash token', color: 'text-green-600' };
      case 'debit':
        return { label: 'Cash token', color: 'text-red-600' };
      case 'credit':
        return { label: 'Cash token', color: 'text-green-600' };
      case 'deposit':
        return { label: 'Deposit', color: 'text-green-600' };
      case 'withdraw':
        return { label: 'Withdrawal', color: 'text-red-600' };
      case 'redeem':
        return { label: 'Cash token', color: 'text-blue-600' };
      default:
        return { label: 'Cash token', color: 'text-foreground' };
    }
  };

  const typeDisplay = getTransactionTypeDisplay();
  
  // Get the transaction ID - backend now provides transaction.id
  const transactionId = transaction.id || transaction._id || transaction.key || '';

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return "Unknown date";
    }
    
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid date";
    }
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getAmountDisplay = () => {
    const isNegative = transaction.type === 'send' || transaction.type === 'withdraw' || transaction.type === 'debit';
    return (
      <CurrencyDisplay
        amount={transaction.amount}
        currency={transaction.currency}
        size="lg"
        showSign={true}
        variant={isNegative ? 'destructive' : 'success'}
        className={`text-2xl font-bold ${isNegative ? 'text-red-600' : 'text-green-600'}`}
      />
    );
  };

  const handleCopyTransactionId = () => {
    navigator.clipboard.writeText(transactionId);
    toast({
      title: 'Copied!',
      description: 'Transaction ID copied to clipboard',
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Transaction Details',
        text: `Transaction ${transactionId}`,
        url: window.location.href,
      });
    } else {
      const shareText = `Transaction ID: ${transactionId}\nAmount: ${transaction.amount} ${transaction.currency}\nStatus: ${transaction.status}`;
      navigator.clipboard.writeText(shareText);
      toast({
        title: 'Copied!',
        description: 'Transaction details copied to clipboard',
      });
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm animate-in slide-in-from-top-2 duration-300 mx-4 my-2">
      {/* Header with close button */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-full ${
            typeDisplay.color === 'text-red-600' ? 'bg-red-100' : 
            typeDisplay.color === 'text-green-600' ? 'bg-green-100' : 
            typeDisplay.color === 'text-orange-600' ? 'bg-orange-100' :
            typeDisplay.color === 'text-purple-600' ? 'bg-purple-100' :
            typeDisplay.color === 'text-indigo-600' ? 'bg-indigo-100' :
            'bg-blue-100'
          }`}>
            {typeDisplay.label.includes('Invoice') ? (
              <Receipt className={`w-5 h-5 ${typeDisplay.color.replace('text-', 'text-')}`} />
            ) : transaction.type === 'send' || transaction.type === 'debit' ? (
              <ArrowUpRight className="w-5 h-5 text-red-600" />
            ) : (
              <ArrowDownLeft className="w-5 h-5 text-green-600" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold">Transaction Details</h3>
            <Badge variant="outline" className={`text-sm ${typeDisplay.color}`}>
              {typeDisplay.label}
            </Badge>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

          {/* Amount Display */}
      <div className="px-4 py-2 text-center border-b bg-gray-50">
            {getAmountDisplay()}
          </div>

      {/* Transaction Details Grid */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Column */}
          <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Transaction ID</label>
                  <div className="flex items-center space-x-2 mt-1">
                <span className="text-sm font-mono">{transactionId}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyTransactionId}
                      className="h-6 w-6 p-0"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="mt-1 space-y-1">
                    <StatusBadge status={
                  // Map backend status to valid StatusBadge status
                  (() => {
                    // Deposits/topups that appear in transaction list are always completed
                    // (they only appear after successful processing)
                    if (transaction.type === 'deposit' || 
                        transaction.type === 'credit' || 
                        transaction.tokenType === 'topup' || 
                        transaction.tokenType === 'balance_up') {
                      return 'completed';
                    }
                    
                    const status = transaction.status?.toLowerCase() || '';
                    // Check if reason is "expired" - if so, show as expired even if status is "failed"
                    const reason = (transaction as any).reason?.toLowerCase() || '';
                    if (status === 'completed' || status === 'redeemed') return 'completed';
                    if (reason === 'expired' || status === 'expired') return 'expired';
                    if (status === 'failed') return 'failed';
                    if (status === 'cancelled') return 'cancelled';
                    if (status === 'active') return 'active';
                    if (status === 'inactive') return 'inactive';
                    return 'pending';
                  })()
                } />
                    {/* 
                     * Backend inconsistency: Invoice tokens that are fully paid are placed in the 
                     * "completed" array but their status field remains "pending". This creates a 
                     * visual inconsistency between list and detail views. Show additional indicator 
                     * to clarify the payment status.
                     */}
                    {transaction.type === 'invoice' && (transaction as any).isFullyPaid && transaction.status === 'pending' && (
                      <div className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        <span>Invoice fully paid</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Type</label>
              <p className={`text-sm font-medium ${typeDisplay.color}`}>
                {typeDisplay.label}
              </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">Currency</label>
              <p className="text-sm">{transaction.currency}</p>
            </div>

            {/* Topup Source Information */}
            {transaction.tokenType === 'balance_up' && (transaction as any).source && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Payment Method</label>
                <p className="text-sm capitalize">
                  {(transaction as any).source === 'paypal' ? 'PayPal' : 
                   (transaction as any).source === 'stripe' ? 'Stripe' : 
                   (transaction as any).source === 'ach' || (transaction as any).source === 'bank' ? 'Bank Transfer' : 
                   (transaction as any).source}
                </p>
              </div>
            )}
                </div>

          {/* Right Column */}
          <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created</label>
              <p className="text-sm">{formatDate(transaction.date)}</p>
                </div>

            {transaction.updatedAt && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Updated</label>
                <p className="text-sm">{formatDate(transaction.updatedAt)}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-muted-foreground">Message</label>
              <p className="text-sm whitespace-pre-wrap">
                {/* For PENDING: message is in data.message, for COMPLETED: message is top-level */}
                {transaction.status === 'PENDING' 
                  ? (transaction.data?.message || transaction.message || transaction.description || 'No message provided')
                  : (transaction.message || transaction.data?.message || transaction.description || 'No message provided')
                }
              </p>
            </div>

            {/* Transaction Direction (for completed transactions) */}
            {transaction.status === 'COMPLETED' && typeof transaction.recipient === 'boolean' && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Direction</label>
                <div className="flex items-center gap-2">
                  {transaction.recipient ? (
                    <>
                      <ArrowDownCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-600">Received</span>
                    </>
                  ) : (
                    <>
                      <ArrowUpCircle className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-600">Sent</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Recipient Information */}
            {(transaction.recipientName || transaction.recipientId) && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Recipient</label>
                <p className="text-sm">
                  {user?._id && transaction.recipientId === user._id
                    ? 'me'
                    : transaction.recipientName ||
                      formatPartyUserIdForDisplay(transaction.recipientId, user?._id) ||
                      'Unknown'}
                </p>
              </div>
            )}

            {/* Invoice Payment Indicator for Cash Tokens */}
            {transaction.type === 'cash' && (
              (transaction as any).paidInvoice || 
              (transaction as any).invoiceKey || 
              (transaction as any).invoice_key ||
              (transaction as any).purpose?.includes('invoice') ||
              (transaction as any).metadata?.type === 'invoice_payment' ||
              (transaction as any).metadata?.paidInvoice
            ) && (
              <div className="mt-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-xs text-orange-700 font-medium flex items-center">
                  <Receipt className="w-3 h-3 mr-1" />
                  This cash token was used to pay an invoice
                  {((transaction as any).paidInvoice || (transaction as any).invoiceKey || (transaction as any).invoice_key) && (
                    <span className="ml-1">
                      ({(transaction as any).paidInvoice || (transaction as any).invoiceKey || (transaction as any).invoice_key})
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Token Key / Code */}
            {(transaction.tokenKey || transaction.code) && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Token Code</label>
                <p className="text-sm font-mono">{transaction.tokenKey || transaction.code}</p>
              </div>
            )}

            {/* Payload Data - Different fields for PENDING vs COMPLETED */}
            {transaction.status === 'PENDING' && transaction.payload && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Token Payload</label>
                <div className="mt-1 p-2 bg-gray-50 rounded-lg">
                  <pre className="text-xs overflow-x-auto">
                    {JSON.stringify(transaction.payload, null, 2)}
                  </pre>
                </div>
              </div>
            )}
            
            {/* For COMPLETED transactions, show both originator and redeemer payloads */}
            {transaction.status === 'COMPLETED' && transaction.originatorPayload && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Original Token Payload</label>
                <div className="mt-1 p-2 bg-gray-50 rounded-lg">
                  <pre className="text-xs overflow-x-auto">
                    {JSON.stringify(transaction.originatorPayload, null, 2)}
                  </pre>
                </div>
              </div>
            )}
            
            {transaction.status === 'COMPLETED' && transaction.redeemerPayload && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Redemption Payload</label>
                <div className="mt-1 p-2 bg-green-50 rounded-lg">
                  <pre className="text-xs overflow-x-auto">
                    {JSON.stringify(transaction.redeemerPayload, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Additional Token Data (from data field) */}
            {transaction.data && Object.keys(transaction.data).length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Token Data</label>
                <div className="mt-1 p-2 bg-blue-50 rounded-lg">
                  {transaction.data.amount && (
                    <p className="text-xs mb-1">
                      <span className="font-medium">Amount:</span> {transaction.data.amount} {transaction.data.currency || transaction.currency}
                    </p>
                  )}
                  {transaction.data.validity && (
                    <p className="text-xs mb-1">
                      <span className="font-medium">Validity:</span> {JSON.stringify(transaction.data.validity)}
                    </p>
                  )}
                  {/* Show any other fields in data that aren't already displayed */}
                  {Object.entries(transaction.data).map(([key, value]) => {
                    if (key === 'message' || key === 'amount' || key === 'currency' || key === 'validity' || key === 'payload') return null;
                    return (
                      <p key={key} className="text-xs mb-1">
                        <span className="font-medium">{key}:</span> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </p>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Funding Source for Token Creation - Removed as per backend changes */}
            {/* {transaction.fundingSource && (transaction.type === 'cash' || transaction.type === 'invoice') && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Funded With</label>
                <div className="mt-1">
                  <span className="inline-flex items-center space-x-1 text-sm">
                    <CreditCard className="w-4 h-4 text-blue-500" />
                    <span>
                      {transaction.fundingSource.brand || transaction.fundingSource.source}
                      {transaction.fundingSource.last4 && ` •••• ${transaction.fundingSource.last4}`}
                    </span>
                    {transaction.fundingSource.amount && (
                      <span className="text-gray-500">
                        ({transaction.fundingSource.currency} {transaction.fundingSource.amount.toFixed(2)})
                      </span>
                    )}
                  </span>
                </div>
              </div>
            )} */}

            {/* Recipient / Originator — only for string ids; boolean `recipient` is used for Direction only */}
            {(() => {
              const r = transaction.recipient;
              const o = transaction.originator;
              const partyString =
                typeof r === 'string' ? r : typeof o === 'string' ? o : undefined;
              if (!partyString) return null;
              const structuredRecipient = Boolean(
                transaction.recipientName || transaction.recipientId
              );
              if (
                structuredRecipient &&
                typeof r === 'string' &&
                transaction.recipientId &&
                r === transaction.recipientId
              ) {
                return null;
              }
              const label = typeof r === 'string' ? 'Recipient' : 'Originator';
              return (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{label}</label>
                  <p className="text-sm">
                    {formatPartyUserIdForDisplay(partyString, user?._id)}
                  </p>
                </div>
              );
            })()}

            {/* Counterparty */}
            {transaction.counterparty && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Counterparty</label>
                <p className="text-sm">{transaction.counterparty.name}</p>
                    {transaction.counterparty.email && (
                  <p className="text-xs text-muted-foreground">{transaction.counterparty.email}</p>
                    )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Applied Cash Tokens Summary (for Invoice tokens) */}
      {transaction.tokenKey && detailedTokenInfo?.invoicePayments && detailedTokenInfo.invoicePayments.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h4 className="font-medium mb-3 flex items-center">
              <Receipt className="w-4 h-4 mr-2 text-orange-600" />
              {detailedTokenInfo.invoiceMetadata?.createdByCurrentUser ? 
                'Invoice Payments Received' : 
                'Invoice Payment Summary'
              }
            </h4>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-orange-800">
                  {detailedTokenInfo.invoicePayments.length} Payment{detailedTokenInfo.invoicePayments.length !== 1 ? 's' : ''} Applied
                </span>
                <span className="text-lg font-bold text-orange-700">
                  <CurrencyDisplay 
                    amount={detailedTokenInfo.invoicePayments.reduce((sum, p) => sum + p.amount, 0)} 
                    currency={detailedTokenInfo.invoicePayments[0]?.currency || 'USD'} 
                    showSign={true}
                  />
                </span>
              </div>
              
              <div className="flex flex-wrap gap-2 mb-3">
                {detailedTokenInfo.invoicePayments.map((payment, index) => (
                  <div key={payment.paymentId} className="inline-flex items-center px-2 py-1 bg-white rounded border border-orange-300 text-xs">
                    <div className="flex items-center space-x-1">
                      <span className="font-mono font-bold text-blue-600">{payment.tokenKey || payment.transactionId.slice(-6)}</span>
                      <span className="text-orange-700">
                        <CurrencyDisplay amount={payment.amount} currency={payment.currency} currencyService={currencyService} />
                      </span>
                      {payment.paidByCurrentUser && (
                        <span className="text-blue-600 font-bold">•YOU</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Payment Methods Summary */}
              <div className="text-xs text-orange-700">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <span className="font-medium">Payment Methods:</span>
                    <div className="flex space-x-1">
                      {Array.from(new Set(detailedTokenInfo.invoicePayments.map(p => p.paymentMethod))).map(method => (
                        <span key={method} className="px-1 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                          {method === 'cash_token' ? 'Cash Token' : 
                           method === 'balance_debit' ? 'Balance' :
                           method}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {detailedTokenInfo.invoiceMetadata && (
                    <div className="flex items-center space-x-1">
                      <span className="font-medium">Status:</span>
                      <span className={`px-1 py-0.5 rounded text-xs ${
                        detailedTokenInfo.invoiceMetadata.isFullyPaid 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {detailedTokenInfo.invoiceMetadata.isFullyPaid ? 'Paid' : 'Partial'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Token Information */}
      {transaction.tokenKey && (
        <Card>
          <CardContent className="p-4">
            <h4 className="font-medium mb-3">Token Information</h4>
            <TokenCard
              rawBackendData={transaction}
              theme="minimal"
              showActions={transaction.status !== 'completed' && transaction.status !== 'redeemed'}
              showQRCode={true}
              qrMode="direct"
              className="scale-90 origin-left"
            />
            
            {/* QR Code Display - Show prominently for active tokens */}
            {(transaction.status !== 'completed' && transaction.status !== 'redeemed' && transaction.status !== 'expired') && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-sm flex items-center">
                    <QrCode className="w-4 h-4 mr-2" />
                    QR Code
                  </h5>
                </div>
                <div className="flex justify-center">
                  <div className="p-3 bg-white rounded-lg border-2 border-gray-300">
                    <QRCode 
                      value={`https://deposit.${window.location.host}/${transaction.tokenKey || transaction.key}`}
                      size={160}
                      title={`QR Code for token ${transaction.tokenKey || transaction.key}`}
                    />
                  </div>
                </div>
                <p className="text-xs text-center text-muted-foreground mt-3">
                  Scan this QR code to redeem the token
                </p>
              </div>
            )}
            
            <div className="mt-3 text-sm text-muted-foreground">
              <p>• Token can be shared via QR code or 6-character code</p>
              <p>• {transaction.status === 'completed' || transaction.status === 'redeemed' ? 'This token has been redeemed' : 'Token is active and ready to use'}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoice Payment History */}
      {detailedTokenInfo?.invoicePayments && detailedTokenInfo.invoicePayments.length > 0 && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Receipt className="w-5 h-5 text-orange-600" />
              <span>
                {detailedTokenInfo.invoiceMetadata?.createdByCurrentUser ? 
                  'Payments Received' : 
                  'Your Payments'
                }
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Payment Summary */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-orange-800">
                  {detailedTokenInfo.invoicePayments.length} Payment{detailedTokenInfo.invoicePayments.length !== 1 ? 's' : ''} Applied
                </span>
                <span className="text-lg font-bold text-orange-700">
                  <CurrencyDisplay 
                    amount={detailedTokenInfo.invoicePayments.reduce((sum, p) => sum + p.amount, 0)} 
                    currency={detailedTokenInfo.invoicePayments[0]?.currency || 'USD'} 
                    showSign={true}
                  />
                </span>
              </div>

              {/* Payment Status */}
              {detailedTokenInfo.invoiceMetadata && (
                <div className="flex items-center space-x-1 text-sm">
                  <span className="font-medium text-orange-800">Status:</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    detailedTokenInfo.invoiceMetadata.isFullyPaid 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {detailedTokenInfo.invoiceMetadata.isFullyPaid ? 'Fully Paid' : 'Partially Paid'}
                  </span>
                  {!detailedTokenInfo.invoiceMetadata.isFullyPaid && (
                    <span className="text-orange-700">
                      (Remaining: <CurrencyDisplay amount={detailedTokenInfo.invoiceMetadata.remainingAmount} currency={detailedTokenInfo.invoicePayments[0]?.currency || 'USD'} currencyService={currencyService} />)
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Detailed Payment List */}
            <div className="space-y-3">
              {detailedTokenInfo.invoicePayments.map((payment, index) => (
                <div key={payment.paymentId} className="bg-white rounded-lg p-4 border border-orange-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        payment.paidByCurrentUser ? 'bg-blue-100' : 'bg-green-100'
                      }`}>
                        <span className={`font-bold text-sm ${
                          payment.paidByCurrentUser ? 'text-blue-600' : 'text-green-600'
                        }`}>
                          {payment.paidByCurrentUser ? 'YOU' : `#${index + 1}`}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="font-bold text-lg text-gray-900">
                            {payment.paymentMethod === 'cash_token' ? 'Cash Token: ' : 'Payment: '}
                            <span className="font-mono text-blue-600">{payment.tokenKey || payment.transactionId}</span>
                          </p>
                          {payment.paidByCurrentUser && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              Your Payment
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{formatDate(payment.paidAt)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-green-600">
                        <CurrencyDisplay amount={payment.amount} currency={payment.currency} currencyService={currencyService} showSign={true} />
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm bg-gray-50 rounded p-3">
                    <div>
                      <label className="font-medium text-gray-700">Payment Method</label>
                      <p className="text-gray-600">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {payment.paymentMethod === 'cash_token' ? 'Cash Token' : 
                           payment.paymentMethod === 'balance_debit' ? 'Account Balance' :
                           payment.paymentMethod}
                        </span>
                      </p>
                    </div>

                    {/* Funding Source Information - Removed as per backend changes */}
                    {/* {payment.fundingSource && (
                      <div>
                        <label className="font-medium text-gray-700">Funding Source</label>
                        <p className="text-gray-600">
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                            {payment.fundingSource === 'user_balance' ? 'Account Balance' :
                             payment.fundingSource === 'stripe' ? 'Credit Card' :
                             payment.fundingSource === 'paypal' ? 'PayPal' :
                             payment.fundingSource === 'plaid' ? 'Bank Account' :
                             payment.fundingSource}
                          </span>
                        </p>
                      </div>
                    )} */}

                    <div>
                      <label className="font-medium text-gray-700">Paid By</label>
                      <p className="text-gray-600">
                        {payment.paidByCurrentUser ? 'You' : payment.paidBy}
                      </p>
                    </div>

                    <div>
                      <label className="font-medium text-gray-700">Date</label>
                      <p className="text-gray-600 text-xs">{formatDate(payment.paidAt)}</p>
                    </div>
                  </div>

                  {payment.message && (
                    <div className="mt-3 p-2 bg-blue-50 rounded">
                      <p className="text-sm text-blue-700 italic">"{payment.message}"</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Token Information for Completed Tokens */}
      {transaction.tokenKey && (transaction.status === 'completed' || transaction.status === 'redeemed') && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>Completion Details</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingDetails ? (
              <div className="flex items-center justify-center py-4">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-muted-foreground">Loading completion details...</p>
                </div>
              </div>
            ) : detailedTokenInfo ? (
              <div className="space-y-4">
                {/* Redemption Details */}
                {detailedTokenInfo.redemptionDetails && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      Redemption Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <label className="font-medium text-green-700">Redeemed At</label>
                        <p className="text-green-600">{formatDate(detailedTokenInfo.redemptionDetails.redeemedAt)}</p>
                      </div>
                      <div>
                        <label className="font-medium text-green-700">Redeemed By</label>
                        <p className="text-green-600">{detailedTokenInfo.redemptionDetails.redeemedBy}</p>
                      </div>
                      <div>
                        <label className="font-medium text-green-700">Amount Redeemed</label>
                        <p className="text-green-600">
                          <CurrencyDisplay amount={detailedTokenInfo.redemptionDetails.redeemedAmount} currency={detailedTokenInfo.token.currency} currencyService={currencyService} />
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-green-700">Net Amount</label>
                        <p className="text-green-600">
                          <CurrencyDisplay amount={detailedTokenInfo.redemptionDetails.netAmount} currency={detailedTokenInfo.token.currency} currencyService={currencyService} />
                        </p>
                      </div>
                      {detailedTokenInfo.redemptionDetails.fees > 0 && (
                        <div>
                          <label className="font-medium text-green-700">Fees</label>
                          <p className="text-green-600">
                            <CurrencyDisplay amount={detailedTokenInfo.redemptionDetails.fees} currency={detailedTokenInfo.token.currency} currencyService={currencyService} />
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Token Message */}
                {detailedTokenInfo.token.message && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Token Message
                    </h4>
                    <p className="text-blue-700 italic">"{detailedTokenInfo.token.message}"</p>
                  </div>
                )}

                {/* Referral Reward Information */}
                {detailedTokenInfo.referralReward?.isReferralToken && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800 mb-3 flex items-center">
                      <Gift className="w-4 h-4 mr-2" />
                      Referral Reward
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-purple-700">Reward Amount:</span>
                        <span className="text-purple-600">
                          <CurrencyDisplay amount={detailedTokenInfo.referralReward.rewardAmount} currency={detailedTokenInfo.referralReward.rewardCurrency} currencyService={currencyService} />
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-purple-700">Reward Status:</span>
                        <Badge variant={detailedTokenInfo.referralReward.rewardGiven ? "default" : "secondary"}>
                          {detailedTokenInfo.referralReward.rewardGiven ? 'Paid' : 'Pending'}
                        </Badge>
                      </div>
                      {detailedTokenInfo.referralReward.rewardGiven && detailedTokenInfo.referralReward.rewardGivenAt && (
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-purple-700">Reward Given At:</span>
                          <span className="text-purple-600">{formatDate(detailedTokenInfo.referralReward.rewardGivenAt)}</span>
                        </div>
                      )}
                      {detailedTokenInfo.referralReward.newUserReward && (
                        <div className="bg-purple-100 rounded p-2 mt-2">
                          <p className="text-purple-700 text-xs">
                            🎉 This was a new user referral - eligible for bonus rewards!
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Applied Cash Tokens (Invoice Payments) */}
                {detailedTokenInfo.invoicePayments && detailedTokenInfo.invoicePayments.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-800 mb-3 flex items-center">
                      <Receipt className="w-4 h-4 mr-2" />
                      {detailedTokenInfo.invoiceMetadata?.createdByCurrentUser ? 
                        `Payments Received (${detailedTokenInfo.invoicePayments.length})` : 
                        `Applied Cash Tokens (${detailedTokenInfo.invoicePayments.length})`
                      }
                    </h4>
                    
                    <p className="text-sm text-orange-700 mb-3">
                      {detailedTokenInfo.invoiceMetadata?.createdByCurrentUser ?
                        "The following payments have been made for this invoice you created:" :
                        "The following cash tokens have been applied to pay this invoice:"
                      }
                    </p>
                    
                    <div className="space-y-3">
                      {detailedTokenInfo.invoicePayments.map((payment, index) => (
                        <div key={payment.paymentId} className="bg-white rounded-lg p-4 border border-orange-200 shadow-sm">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                payment.paidByCurrentUser ? 'bg-blue-100' : 'bg-green-100'
                              }`}>
                                <span className={`font-bold text-sm ${
                                  payment.paidByCurrentUser ? 'text-blue-600' : 'text-green-600'
                                }`}>
                                  {payment.paidByCurrentUser ? 'YOU' : `#${index + 1}`}
                                </span>
                              </div>
                              <div>
                                <div className="flex items-center space-x-2">
                                  <p className="font-bold text-lg text-gray-900">
                                    {payment.paymentMethod === 'cash_token' ? 'Cash Token: ' : 'Payment: '}
                                    <span className="font-mono text-blue-600">{payment.tokenKey || payment.transactionId}</span>
                                  </p>
                                  {payment.paidByCurrentUser && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                      Your Payment
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{formatDate(payment.paidAt)}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-green-600">
                                <CurrencyDisplay amount={payment.amount} currency={payment.currency} currencyService={currencyService} showSign={true} />
                              </p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm bg-gray-50 rounded p-3">
                            <div>
                              <label className="font-medium text-gray-700">Payment Method</label>
                              <p className="text-gray-600">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                  {payment.paymentMethod === 'cash_token' ? 'Cash Token' : 
                                   payment.paymentMethod === 'balance_debit' ? 'Account Balance' :
                                   payment.paymentMethod}
                                </span>
                              </p>
                            </div>
                            
                            {/* Funding source tracking removed */}
                            
                            <div>
                              <label className="font-medium text-gray-700">Applied By</label>
                              <p className="text-gray-600">
                                {payment.paidByCurrentUser ? 'You' : payment.paidBy}
                              </p>
                            </div>
                            
                            <div>
                              <label className="font-medium text-gray-700">Date Applied</label>
                              <p className="text-gray-600 text-xs">{formatDate(payment.paidAt)}</p>
                            </div>
                          </div>
                          
                          {payment.message && (
                            <div className="mt-3 p-2 bg-blue-50 rounded">
                              <p className="text-sm text-blue-700 italic">"{payment.message}"</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Enhanced Summary */}
                    <div className="mt-4 space-y-3">
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-green-800">Total Paid:</span>
                          <span className="text-lg font-bold text-green-700">
                            <CurrencyDisplay 
                              amount={detailedTokenInfo.invoicePayments.reduce((sum, p) => sum + p.amount, 0)} 
                              currency={detailedTokenInfo.invoicePayments[0]?.currency || 'USD'} 
                              showSign={true}
                            />
                          </span>
                        </div>
                      </div>
                      
                      {detailedTokenInfo.invoiceMetadata && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                            <div>
                              <label className="font-medium text-blue-800">Original Amount:</label>
                              <p className="text-blue-700">
                                <CurrencyDisplay amount={detailedTokenInfo.invoiceMetadata.originalAmount} currency={detailedTokenInfo.invoicePayments[0]?.currency || 'USD'} currencyService={currencyService} />
                              </p>
                            </div>
                            
                            <div>
                              <label className="font-medium text-blue-800">Remaining:</label>
                              <p className="text-blue-700">
                                <CurrencyDisplay amount={detailedTokenInfo.invoiceMetadata.remainingAmount} currency={detailedTokenInfo.invoicePayments[0]?.currency || 'USD'} currencyService={currencyService} />
                              </p>
                            </div>
                            
                            <div>
                              <label className="font-medium text-blue-800">Status:</label>
                              <p className="text-blue-700">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  detailedTokenInfo.invoiceMetadata.isFullyPaid 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {detailedTokenInfo.invoiceMetadata.isFullyPaid ? 'Fully Paid' : 'Partially Paid'}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Prepaid Card Information */}
                {detailedTokenInfo.prepaidCard && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <h4 className="font-semibold text-indigo-800 mb-3 flex items-center">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Prepaid Card Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <label className="font-medium text-indigo-700">Card Status</label>
                        <p className="text-indigo-600">{detailedTokenInfo.prepaidCard.status}</p>
                      </div>
                      <div>
                        <label className="font-medium text-indigo-700">Card Brand</label>
                        <p className="text-indigo-600">{detailedTokenInfo.prepaidCard.cardBrand?.toUpperCase() || 'VISA'}</p>
                      </div>
                      <div>
                        <label className="font-medium text-indigo-700">Amount Loaded</label>
                        <p className="text-indigo-600">
                          <CurrencyDisplay amount={detailedTokenInfo.prepaidCard.amountLoaded} currency={detailedTokenInfo.prepaidCard.currency} currencyService={currencyService} />
                        </p>
                      </div>
                      {detailedTokenInfo.prepaidCard.convertedAt && (
                        <div>
                          <label className="font-medium text-indigo-700">Converted At</label>
                          <p className="text-indigo-600">{formatDate(detailedTokenInfo.prepaidCard.convertedAt)}</p>
                        </div>
                      )}
                    </div>
                    {detailedTokenInfo.token?.key && detailedTokenInfo.prepaidCard?.status === 'active' && (
                      <div className="mt-4 pt-4 border-t border-indigo-200 space-y-2">
                        <p className="text-xs text-indigo-700">
                          Pay at a contactless terminal using the VAIU NFC shell app (tap-to-pay requires a platform tap session and issuer credentials).
                        </p>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={prepaidTapToPay.isBusy}
                          onClick={() =>
                            prepaidTapToPay.startTapToPay({
                              tokenKey: detailedTokenInfo.token.key,
                              uniqueUserId: detailedTokenInfo.prepaidCard.uniqueUserId,
                              tokenId: detailedTokenInfo.prepaidCard.tokenId,
                              amount: detailedTokenInfo.prepaidCard.amountLoaded,
                              currency: detailedTokenInfo.prepaidCard.currency || detailedTokenInfo.prepaidCard.cardCurrency || 'USD',
                            })
                          }
                        >
                          {prepaidTapToPay.isBusy ? 'Starting…' : 'Tap to pay (NFC shell)'}
                        </Button>
                        {prepaidTapToPay.message && (
                          <p className="text-xs text-indigo-800">{prepaidTapToPay.message}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">
                  Additional completion details are not available for this token.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Account Information for Deposits */}
      {transaction.type === 'deposit' && (
        <Card>
          <CardContent className="p-4">
            <h4 className="font-medium mb-3">Funding Source</h4>
            <div className="space-y-3">
              {transaction.description.includes('PayPal') && (
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 font-bold text-sm">PP</span>
                  </div>
                  <div>
                    <p className="font-medium">PayPal Account</p>
                    <p className="text-sm text-muted-foreground">Instant transfer</p>
                  </div>
                </div>
              )}

              {transaction.description.includes('Card') && (
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-green-600 font-bold text-sm">💳</span>
                  </div>
                <div>
                    <p className="font-medium">Credit/Debit Card</p>
                    <p className="text-sm text-muted-foreground">
                      {transaction.description.includes('ending in') 
                        ? transaction.description.split('ending in ')[1]?.split(' ')[0] 
                        : 'Card payment'
                      }
                    </p>
                  </div>
                </div>
              )}

              {transaction.description.includes('Bank account') && (
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <span className="text-purple-600 font-bold text-sm">🏦</span>
                  </div>
                <div>
                    <p className="font-medium">Bank Account</p>
                    <p className="text-sm text-muted-foreground">
                      {transaction.description.includes('Bank account: ') 
                        ? transaction.description.split('Bank account: ')[1]?.split(' ')[0] 
                        : 'ACH transfer'
                      }
                    </p>
                  </div>
                </div>
              )}

              {/* Fee Information */}
              {transaction.description.includes('fee') && (
                <div className="mt-3 pt-3 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Processing Fee:</span>
                    <span className="font-medium">
                      {transaction.description.match(/\$[\d.]+\s+fee/)?.[0] || 'See description'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status-specific Information */}
      {transaction.status === 'failed' && (
        <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div className="text-sm">
            <p className="text-red-800 font-medium">Transaction Failed</p>
            <p className="text-red-700">
              This transaction was not completed. No money was transferred.
            </p>
          </div>
        </div>
      )}

      {transaction.status === 'completed' && (
        <div className="flex items-center space-x-1.5 px-2 py-1 bg-green-50 rounded">
          <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
          <p className="text-xs text-green-800">Completed</p>
        </div>
      )}

      {/* Actions */}
      <div className="p-4 border-t">
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handleShare} className="flex-1">
            <Share className="w-4 h-4 mr-2" />
            Share
          </Button>
          {/* Only show Receipt button if token is redeemed/completed */}
          {(transaction.status === 'completed' || transaction.status === 'redeemed') && (
            <Button variant="outline" className="flex-1">
              <Receipt className="w-4 h-4 mr-2" />
              Receipt
            </Button>
          )}
          {transaction.id && (
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setShowMessaging(!showMessaging)}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              {showMessaging ? 'Hide Messages' : 'Messages'}
            </Button>
          )}
        </div>

        {/* WhatsApp-Style Messaging */}
        {showMessaging && transaction.id && (
          <div className="mt-4 pt-4 border-t">
            <WhatsAppStyleMessaging
              transactionId={transaction.id}
              counterpartyName={transaction.counterparty?.name || 'Counterparty'}
              counterpartyEmail={transaction.counterparty?.email}
            />
          </div>
        )}

        {/* Raw Data Toggle */}
        <div className="mt-4 pt-4 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowRawData(!showRawData)}
            className="text-muted-foreground"
          >
            {showRawData ? 'Hide' : 'Show'} Raw Data
          </Button>
          
          {showRawData && (
            <div className="mt-2 space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Full Transaction Object</label>
                <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto mt-1">
                  {JSON.stringify(transaction, null, 2)}
                </pre>
              </div>
              
              {/* Show detailed token info if available */}
              {detailedTokenInfo && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Detailed Token Information</label>
                  <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto mt-1">
                    {JSON.stringify(detailedTokenInfo, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails; 